import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Skeleton } from './ui/skeleton';
import { Grid3x3, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function VolumeHeatmap({ days = 7 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredCell, setHoveredCell] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${API_URL}/api/analytics/volume-heatmap?days=${days}`);
        if (!response.ok) throw new Error('Failed to fetch heatmap data');
        const result = await response.json();
        setData(result);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 300000); // Refresh every 5 minutes
    return () => clearInterval(interval);
  }, [days]);

  if (loading) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Grid3x3 className="w-5 h-5" />
            Trading Activity Heatmap
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[200px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Trading Activity Heatmap</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Failed to load data</p>
        </CardContent>
      </Card>
    );
  }

  // Create a lookup map for the heatmap data
  const heatmapLookup = {};
  data.heatmap.forEach(item => {
    const key = `${item.day}-${item.hour}`;
    heatmapLookup[key] = item;
  });

  const getIntensity = (value, max) => {
    if (!max || !value) return 0;
    return Math.min(value / max, 1);
  };

  const getCellColor = (intensity) => {
    if (intensity === 0) return 'bg-muted/30';
    if (intensity < 0.25) return 'bg-emerald-500/20';
    if (intensity < 0.5) return 'bg-emerald-500/40';
    if (intensity < 0.75) return 'bg-emerald-500/60';
    return 'bg-emerald-500/80';
  };

  return (
    <Card className="h-full" data-testid="volume-heatmap">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Grid3x3 className="w-5 h-5" />
          Trading Activity ({days}d)
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Hovered cell info */}
        <div className="h-8 mb-2 text-center text-sm">
          {hoveredCell ? (
            <span className="text-muted-foreground">
              <span className="font-medium text-foreground">{hoveredCell.day_name}</span> at{' '}
              <span className="font-medium text-foreground">{hoveredCell.hour}:00</span>:{' '}
              <span className="text-emerald-500">{hoveredCell.trade_count} trades</span>
              {hoveredCell.volume_usd > 0 && (
                <span> (${(hoveredCell.volume_usd / 1000).toFixed(1)}K)</span>
              )}
            </span>
          ) : (
            <span className="text-muted-foreground flex items-center justify-center gap-1">
              <Clock className="w-3 h-3" /> Hover for details
            </span>
          )}
        </div>

        {/* Heatmap grid */}
        <div className="overflow-x-auto">
          <div className="min-w-[400px]">
            {/* Hour labels */}
            <div className="flex ml-10 mb-1">
              {HOURS.filter(h => h % 4 === 0).map(hour => (
                <div 
                  key={hour} 
                  className="text-[10px] text-muted-foreground"
                  style={{ width: `${100/6}%`, textAlign: 'left' }}
                >
                  {hour}:00
                </div>
              ))}
            </div>

            {/* Grid rows */}
            {DAYS.map((day, dayIndex) => (
              <div key={day} className="flex items-center gap-1 mb-1">
                <div className="w-8 text-[10px] text-muted-foreground text-right pr-1">
                  {day}
                </div>
                <div className="flex-1 flex gap-[2px]">
                  {HOURS.map(hour => {
                    const cellData = heatmapLookup[`${dayIndex}-${hour}`];
                    const intensity = cellData 
                      ? getIntensity(cellData.trade_count, data.max_trades)
                      : 0;
                    
                    return (
                      <div
                        key={`${dayIndex}-${hour}`}
                        className={`flex-1 h-4 rounded-sm cursor-pointer transition-all hover:ring-1 hover:ring-foreground/50 ${getCellColor(intensity)}`}
                        onMouseEnter={() => setHoveredCell(cellData || { day_name: day, hour, trade_count: 0, volume_usd: 0 })}
                        onMouseLeave={() => setHoveredCell(null)}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center gap-2 mt-3 text-xs text-muted-foreground">
          <span>Low</span>
          <div className="flex gap-1">
            <div className="w-4 h-3 rounded-sm bg-muted/30" />
            <div className="w-4 h-3 rounded-sm bg-emerald-500/20" />
            <div className="w-4 h-3 rounded-sm bg-emerald-500/40" />
            <div className="w-4 h-3 rounded-sm bg-emerald-500/60" />
            <div className="w-4 h-3 rounded-sm bg-emerald-500/80" />
          </div>
          <span>High</span>
        </div>

        {/* Peak times */}
        {data.peak_times?.by_trades?.length > 0 && (
          <div className="mt-3 pt-3 border-t text-xs">
            <div className="text-muted-foreground mb-1">Peak Trading Times:</div>
            <div className="flex flex-wrap gap-2">
              {data.peak_times.by_trades.slice(0, 3).map((peak) => (
                <span key={`${peak.day}-${peak.hour}`} className="px-2 py-1 rounded bg-emerald-500/10 text-emerald-500">
                  {peak.day} {peak.hour}:00
                </span>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
